/**
 * What a pasted connection string will fill, as text that never carries a secret (Option C, C3, plan §3.2 "Paste
 * detection"). Pure: no DOM, no React.
 *
 * The parser (`parseConnectionString`) is reused; this module only describes its result BEFORE anything reaches the form
 * (the start view's detection card, the pane's paste field) and AFTER it was applied (the summary line):
 * - `rows` — what the patch really changes on the draft it lands on (`pasteRows`: the fields the string carries plus
 *   implicit changes such as the authentication, Connect by / Connect using, the storage resource and endpoints),
 *   labelled with the form's own vocabulary and made safe with the shared helpers (`safeServerText`,
 *   `redactSecretText`); every secret the string carries (the password, an account key, a SAS) is a fixed mask.
 * - `unmapped` — the parser's "partial": keys of a recognised format it could not map (they are left out). That is NOT
 *   the same as a missing credential: a string without a password is a full parse, and
 * - `stillNeeded` — the applicable, required fields that stay empty once the string is applied to the engine's draft
 *   (the prefill focus goes to the first of them), e.g. Password after an ADO.NET string without one.
 *
 * The raw text is never kept here, logged or tracked.
 */
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import type { ConnectionStringFormat, ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';
import { parseResultPatch } from '../../services/connection-string/applyParseResultToForm';
import { requiredEmptyFields } from '../state/connectionFieldRules';
import { findEngineCatalogEntry } from '../state/engineCatalog';
import { fieldRuleLabel } from '../fields/fieldLabels';
import { redactSecretText } from '@/types/safeConnectionDisplay';
import { ENCRYPT_STRICT_NOTE } from '@/core/engines/mssqlEncrypt';
import { pasteRows, type PasteRow } from './pasteRows';

export { SECRET_MASK, type PasteRow } from './pasteRows';
export {
  PASTE_FAILED_MESSAGE, PASTE_FORMATS_LINE, PASTE_NOT_RECOGNIZED, PASTE_UNCLOSED_MESSAGE, pasteConflictMessage, pasteFailedMessage,
  pasteRepeatedKeywordMessage, pasteUnhonouredQuoteMessage, pasteUnreadableValueMessage,
} from './pasteRefusalMessages';

type Values = Partial<ConnectionFormValues>;

const MAX_KEY_LENGTH = 60;

export type PastePreview = {
  outcome: 'full' | 'partial';
  engine: DatabaseEngine;
  engineLabel: string;
  format: ConnectionStringFormat;
  /** "SQL Server connection string", "SQLite database file". */
  headline: string;
  rows: PasteRow[];
  unmapped: string[];
  stillNeeded: string[];
  entraHint: boolean;
  /** Recognisably this engine's but unusable as it is (an Azure Storage string without a key or SAS): why. */
  problem?: string;
  /** SQL Server `Encrypt=Strict`: how Jam honours it (encrypted, certificate validated, no TDS 8.0 strict handshake). */
  tlsNote?: string;
};

const FORMAT_LABELS: Record<ConnectionStringFormat, string> = {
  adonet: 'ADO.NET',
  kusto: 'Kusto',
  'azure-storage': 'Azure Storage',
  url: 'URL',
  libpq: 'libpq',
  jdbc: 'JDBC',
  ezconnect: 'EZConnect',
  sqlite_path: 'File path',
  unknown: '',
};

export function formatLabel(format: ConnectionStringFormat): string {
  return FORMAT_LABELS[format];
}

/**
 * Text shaped like a connection string (or a piece of one): the start view shows the non-echoing failure line for it
 * and never repeats it in the no-match line or the announcement. Any of `= ; @ : / \` is enough — a `key=value`, a URL
 * or `jdbc:` / `sqlite:` prefix, `user/pass`, `host:port`, a path (C3 review C3-02: `jdbc:oracle:thin:scott/SECRET` has
 * none of `= ; @ ://` and used to be echoed). No engine label, id, blurb or keyword contains one (pinned by a test).
 */
export function looksLikeConnectionString(text: string): boolean {
  return /[=;@:/\\]/u.test(text);
}

export function engineLabelOf(engine: DatabaseEngine): string {
  return findEngineCatalogEntry(engine)?.label ?? engine;
}

/** "SQL Server, PostgreSQL or MySQL". */
function listAlternatives(labels: readonly string[]): string {
  return labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

/**
 * What the collapsed chip of a pasted string says (final UX review UX-03): the engine it fills, else its format — never
 * a value. "Connection string · PostgreSQL", "Connection string · ADO.NET", "Connection string".
 */
export function pastedChipLabel(preview: Pick<PastePreview, 'engineLabel'> | null, format: ConnectionStringFormat | undefined): string {
  if (preview) return `Connection string · ${preview.engineLabel}`;
  const label = format ? FORMAT_LABELS[format] : '';
  return label ? `Connection string · ${label}` : 'Connection string';
}

/** The title of a string several engines read alike, before one is picked ("ADO.NET connection string"). */
export function choiceHeadline(format: ConnectionStringFormat): string {
  return FORMAT_LABELS[format] ? `${FORMAT_LABELS[format]} connection string` : 'Connection string';
}

/** What the card says (and announces) until an engine is picked (the ruling after r5: asked, never guessed). */
export function pasteChoiceMessage(engines: readonly DatabaseEngine[]): string {
  return `${listAlternatives(engines.map(engineLabelOf))} can all read it. Choose the engine to see what it fills.`;
}

function safeKey(key: string): string {
  const safe = redactSecretText(key.trim());
  return safe.length > MAX_KEY_LENGTH ? `${safe.slice(0, MAX_KEY_LENGTH - 1)}…` : safe;
}

/**
 * The preview of `result` applied to `base` — the target engine's values before the patch (its cached draft, a first
 * visit's seed, or the live form when it is that engine): the rows are the patch's diff against it (`pasteRows`). Null
 * when the string names no engine (nothing to apply).
 */
export function buildPastePreview(result: ConnectionStringParseResult, base: Values = {}): PastePreview | null {
  const parsed = parseResultPatch(result);
  if (!parsed || result.outcome === 'failed') return null;
  const { engine, patch } = parsed;
  const values: Values = { ...base, ...patch, engine };
  const engineLabel = engineLabelOf(engine);
  // An unusable string's reason is the result's `problem` (the Kusto and Azure Storage parsers, C3-11); it maps nothing.
  const unmapped = result.unmatchedKeys.map(safeKey).filter(Boolean);
  const problem = result.problem;
  return {
    outcome: result.outcome,
    engine,
    engineLabel,
    format: result.format,
    headline: result.format === 'sqlite_path' ? `${engineLabel} database file` : `${engineLabel} connection string`,
    rows: pasteRows(engine, patch, result.values, base, values),
    unmapped,
    stillNeeded: requiredEmptyFields(values).map((rule) => fieldRuleLabel(rule.field, values)),
    entraHint: result.suggestEntra === true,
    ...(problem ? { problem } : {}),
    ...(result.encryptStrict ? { tlsNote: ENCRYPT_STRICT_NOTE } : {}),
  };
}

/** "Host, Port and Database" — a readable list of labels. */
export function listLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** The detection's announcement and one-line text: labels only, never a value. */
export function previewSentence(preview: PastePreview): string {
  const fills = preview.rows.length > 0 ? `fills ${listLabels(preview.rows.map((row) => row.label))}` : 'fills nothing on its own';
  return `${preview.headline}: ${fills}.`;
}

/** What the pane's summary line says after a string was applied or an Azure resource picked (labels only). */
export type AppliedPaste = {
  engine: DatabaseEngine;
  /**
   * Where it came from: the start filter or the pane field (pasted), a palette / SAS prefill handoff, or a Browse Azure
   * pick (final UX review UX-01).
   */
  via: 'pasted' | 'prefill' | 'azure';
  labels: string[];
  unmapped: string[];
  problem?: string;
  entraHint: boolean;
  /** See `PastePreview.tlsNote`. */
  tlsNote?: string;
  /** An Azure pick: the resource's name, made safe. */
  resourceName?: string;
};

/**
 * The summary of a Browse Azure pick (final UX review UX-01): the fields the resource filled — Name when the pick
 * suggested it, the resource's own server, port and database, and whatever else the patch changed on the draft it landed
 * on (authentication, SSL, Encrypt, storage endpoints …) — labelled as a paste's rows are, and the resource's name
 * through the redactor.
 */
export function appliedAzurePick(resourceName: string, engine: DatabaseEngine, patch: Values, base: Values): AppliedPaste {
  const after: Values = { ...base, ...patch, engine };
  const carried: Values = { server: patch.server, port: patch.port, database: patch.database };
  const labels = [...(patch.alias !== undefined ? ['Name'] : []), ...pasteRows(engine, patch, carried, base, after).map((row) => row.label)];
  return {
    engine,
    via: 'azure',
    labels,
    unmapped: [],
    entraHint: false,
    resourceName: redactSecretText(resourceName),
  };
}

export function appliedPaste(preview: PastePreview, via: AppliedPaste['via']): AppliedPaste {
  return {
    engine: preview.engine,
    via,
    labels: preview.rows.map((row) => row.label),
    unmapped: preview.unmapped,
    ...(preview.problem ? { problem: preview.problem } : {}),
    entraHint: preview.entraHint,
    ...(preview.tlsNote ? { tlsNote: preview.tlsNote } : {}),
  };
}
